import { useState, useEffect, useRef } from "react";
import InstagramCarousel from "../InstagramCarousel";
import ExpandedCaption from "./ExpandedCaption";
import instagram_logo from "../../../assets/socialglass/instagram_icon.svg";

function InstagramComponent({
  type,
  downloaded_path,
  muted,
  url,
  creatorName,
  videoRef,
  caption,
  onManualUnmute,
  onManualMute,
  showIcon = false,
  isAutoPlay = true,
  rounded = false,
}) {
  const localRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isCarousel =
    Array.isArray(downloaded_path) && downloaded_path.length > 1;
  const mediaSources = Array.isArray(downloaded_path)
    ? downloaded_path
    : [downloaded_path];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.5 },
    );

    const el = localRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  useEffect(() => {
    const vid = localRef.current;
    if (!vid) return;

    vid.muted = muted;
    setIsMuted(muted);

    const handleVolumeChange = () => {
      if (vid.muted) onManualMute?.();
      else onManualUnmute?.();
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    vid.addEventListener("volumechange", handleVolumeChange);
    vid.addEventListener("play", handlePlay);
    vid.addEventListener("pause", handlePause);

    return () => {
      vid.removeEventListener("volumechange", handleVolumeChange);
      vid.removeEventListener("play", handlePlay);
      vid.removeEventListener("pause", handlePause);
    };
  }, [muted, onManualUnmute, onManualMute]);

  const handlePlayPause = () => {
    const vid = localRef.current;
    if (vid && vid.tagName === "VIDEO") {
      if (isPlaying) {
        vid.pause();
        setIsPlaying(false);
      } else {
        vid.play();
        setIsPlaying(true);
      }
    }
  };

  const handleMuteToggle = (e) => {
    e.stopPropagation();
    const vid = localRef.current;
    if (vid) {
      const newMute = !vid.muted;
      vid.muted = newMute;
      setIsMuted(newMute);
      newMute ? onManualMute?.() : onManualUnmute?.();
    }
  };

  if (!url) return <p>Invalid URL</p>;

  if (isCarousel) {
    return (
      <InstagramCarousel
        type={type}
        downloaded_path={downloaded_path}
        videoRef={(el) => {
          if (videoRef) videoRef(el);
          localRef.current = el;
        }}
        muted={muted}
        onManualUnmute={onManualUnmute}
        onManualMute={onManualMute}
        caption={caption}
        creatorName={creatorName}
      />
    );
  }

  // Truncate caption text if it's too long
  const maxCaptionLength = 100; // Max length before truncation
  const truncatedCaption =
    caption && caption.length > maxCaptionLength
      ? caption.substring(0, maxCaptionLength) + "..."
      : caption;

  return (
    <div
      className={`relative flex aspect-[1/1.75] ${rounded && "rounded-lg"} h-full w-full cursor-pointer items-center justify-center overflow-hidden bg-black`}
      onClick={handlePlayPause}
    >
      {showIcon && (
        <img
          src={instagram_logo}
          alt="Instagram Logo"
          className="absolute left-2 top-2 h-6 w-6"
        />
      )}
      {downloaded_path ? (
        (() => {
          const source = `https://genz-staging.feeltiptop.com/${mediaSources[0]}`;
          const ext = mediaSources[0]?.split(".").pop().toLowerCase();
          const isVideo = ["mp4", "webm", "ogg"].includes(ext);

          return (
            <>
              {isVideo ? (
                <video
                  ref={(el) => {
                    if (videoRef) videoRef(el);
                    localRef.current = el;
                  }}
                  src={source}
                  muted={muted}
                  {...(isAutoPlay ? { autoPlay: true } : {})}
                  loop
                  className="w-full cursor-pointer object-cover"
                  playsInline
                  webkit-playsinline="true"
                />
              ) : (
                <img
                  ref={localRef}
                  src={source}
                  alt="Instagram media"
                  className="w-full object-cover"
                />
              )}
              {/* Caption and Creator Name Overlay, conditional */}
              {showOverlay && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "2rem",
                    left: "1rem",
                    right: "1rem",
                    color: "white",
                    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.7)",
                    fontSize: "12px",
                    zIndex: 30,
                  }}
                >
                  {creatorName && (
                    <p
                      style={{
                        marginDown: "1rem",
                        fontWeight: "bold",
                        textAlign: "left",
                      }}
                    >
                      @{creatorName}
                    </p>
                  )}
                  {caption && (
                    <div>
                      {!isExpanded && (
                        <p style={{ margin: 0, textAlign: "left" }}>
                          {truncatedCaption}
                          {caption.length > maxCaptionLength && (
                            <button
                              onClick={() => setIsExpanded(true)}
                              style={{
                                background: "transparent",
                                color: "#fff",
                                border: "none",
                                fontSize: "14px",
                                fontWeight: "bold",
                                cursor: "pointer",
                                textDecoration: "underline",
                                paddingLeft: "4px",
                              }}
                            >
                              more
                            </button>
                          )}
                        </p>
                      )}
                      {isExpanded && (
                        <ExpandedCaption
                          caption={caption}
                          onCollapse={() => setIsExpanded(false)}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              <div
                className={`absolute inset-0 bg-black transition-opacity duration-500 ${
                  isVideo
                    ? isPlaying
                      ? "opacity-0"
                      : "opacity-60"
                    : isVisible
                      ? "opacity-0"
                      : "opacity-60"
                }`}
              />

              {isVideo && (
                <button
                  onClick={handleMuteToggle}
                  className="absolute right-2 top-2 rounded-full bg-gray-700 p-2 text-white"
                >
                  {isMuted ? "🔇" : "🔊"}
                </button>
              )}
            </>
          );
        })()
      ) : (
        <blockquote
          className="instagram-embed m-0 w-full border-none p-0"
          cite={url}
          data-video-id={url.split("/video/")[1]}
        >
          <section>
            <a href={url} target="_blank" rel="noopener noreferrer" />
          </section>
        </blockquote>
      )}
    </div>
  );
}

export default InstagramComponent;
